import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '@/integrations/supabase/client';
import { ApiResult, ok, err, ApiError } from '../errors';
import * as tus from 'tus-js-client';
import { query, mutate } from '../client';

// ============ Admin Articles ============

export interface AdminArticle {
  id: string;
  title: string;
  excerpt?: string | null;
  content: string;
  category: string;
  language: string;
  published: boolean | null;
  enriched: boolean | null;
  cover_image?: string | null;
  created_at: string | null;
  author_id?: string | null;
  product_id?: string | null;
}

export async function getAdminArticles(language?: string): Promise<ApiResult<AdminArticle[]>> {
  let queryBuilder = supabase
    .from('articles')
    .select('*')
    .order('created_at', { ascending: false });

  if (language && language !== 'all') {
    queryBuilder = queryBuilder.eq('language', language);
  }

  return query(async () => queryBuilder);
}

export async function getAdminArticleById(id: string): Promise<ApiResult<AdminArticle>> {
  return query(async () =>
    supabase
      .from('articles')
      .select('*')
      .eq('id', id)
      .single()
  );
}

export async function createArticle(data: Omit<AdminArticle, 'id' | 'created_at' | 'enriched'>): Promise<ApiResult<AdminArticle>> {
  return mutate(async () =>
    supabase
      .from('articles')
      .insert([data])
      .select()
      .single()
  );
}

export async function updateArticle(id: string, updates: Partial<AdminArticle>): Promise<ApiResult<AdminArticle>> {
  return mutate(async () =>
    supabase
      .from('articles')
      .update(updates)
      .eq('id', id)
      .select()
      .single()
  );
}

export async function deleteArticle(id: string): Promise<ApiResult<null>> {
  const result = await supabase.from('articles').delete().eq('id', id);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function toggleArticlePublish(id: string, published: boolean): Promise<ApiResult<null>> {
  const result = await supabase.from('articles').update({ published }).eq('id', id);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function duplicateArticle(id: string): Promise<ApiResult<AdminArticle>> {
  // Fetch the original article
  const articleResult = await getAdminArticleById(id);
  if (!articleResult.success) return articleResult;

  const original = articleResult.data;

  // Create a duplicate with "Copy of" prefix
  const duplicate: Omit<AdminArticle, 'id' | 'created_at' | 'enriched'> = {
    title: `Copy of ${original.title}`,
    excerpt: original.excerpt,
    content: original.content,
    cover_image: original.cover_image,
    category: original.category,
    language: original.language,
    author_id: original.author_id,
    published: false, // Duplicates start as unpublished
  };

  return createArticle(duplicate);
}

// ============ Admin Programs ============

export interface AdminProgram {
  id: string;
  name: string;
  description: string | null;
  language: string;
  is_active: boolean;
  group_id: string | null;
  product_id: string | null;
  cover_image: string | null;
  created_at: string;
  group?: { name: string } | null;
  product?: { name: string; slug: string } | null;
  program_phases?: Array<{
    id: string;
    name: string;
    description?: string | null;
    order_index: number;
    duration_days?: number | null;
    unlock_type: string;
    phase_activities?: Array<{
      id: string;
      activity_type: string;
      activity_id: string;
      order_index: number;
      is_required: boolean;
      title_override?: string | null;
    }>;
  }>;
  phases_count?: number;
  activities_count?: number;
}

export async function getAdminPrograms(): Promise<ApiResult<AdminProgram[]>> {
  const result = await supabase
    .from('programs')
    .select(`*, group:groups(name), product:products(name, slug), program_phases(id, phase_activities(id))`)
    .order('created_at', { ascending: false });

  if (result.error) return err(ApiError.fromSupabaseError(result.error));

  const programsWithCount = (result.data || []).map((p: any) => ({
    ...p,
    program_phases: undefined, // Clear for count-only query
    phases_count: p.program_phases?.length || 0,
    activities_count: p.program_phases?.reduce((sum: number, phase: any) => sum + (phase.phase_activities?.length || 0), 0) || 0,
  })) as AdminProgram[];

  return ok(programsWithCount);
}

export async function getAdminProgramById(id: string): Promise<ApiResult<AdminProgram>> {
  return query(async () =>
    supabase
      .from('programs')
      .select(`*, program_phases(*, phase_activities(*))`)
      .eq('id', id)
      .single()
  );
}

export async function createProgram(data: { name: string; description?: string | null; language: string; is_active: boolean; group_id?: string | null; product_id?: string | null; cover_image?: string | null }): Promise<ApiResult<{ id: string }>> {
  return mutate(async () =>
    supabase
      .from('programs')
      .insert(data)
      .select('id')
      .single()
  );
}

export async function updateProgram(id: string, data: Partial<AdminProgram>): Promise<ApiResult<null>> {
  const result = await supabase.from('programs').update(data).eq('id', id);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function deleteProgram(id: string): Promise<ApiResult<null>> {
  const result = await supabase.from('programs').delete().eq('id', id);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function toggleProgramActive(id: string, isActive: boolean): Promise<ApiResult<null>> {
  const result = await supabase.from('programs').update({ is_active: isActive }).eq('id', id);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function deleteProgramPhases(programId: string): Promise<ApiResult<null>> {
  const result = await supabase.from('program_phases').delete().eq('program_id', programId);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function createProgramPhase(data: { program_id: string; name: string; description?: string | null; order_index: number; duration_days?: number | null; unlock_type: string }): Promise<ApiResult<{ id: string }>> {
  return mutate(async () =>
    supabase
      .from('program_phases')
      .insert(data)
      .select('id')
      .single()
  );
}

export async function createPhaseActivities(activities: Array<{ phase_id: string; activity_type: string; activity_id: string; order_index: number; is_required: boolean; title_override?: string | null }>): Promise<ApiResult<null>> {
  const result = await supabase.from('phase_activities').insert(activities);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function duplicateProgram(programId: string): Promise<ApiResult<string>> {
  const { data: program, error: fetchError } = await supabase
    .from('programs')
    .select(`*, program_phases(*, phase_activities(*))`)
    .eq('id', programId)
    .single();

  if (fetchError || !program) return err(ApiError.fromSupabaseError(fetchError || { message: 'Program not found' }));

  const { data: newProgram, error: programError } = await supabase
    .from('programs')
    .insert({ name: `${program.name} (Copy)`, description: program.description, language: program.language, is_active: false, group_id: program.group_id })
    .select('id')
    .single();

  if (programError || !newProgram) return err(ApiError.fromSupabaseError(programError || { message: 'Failed to create program' }));

  for (const phase of (program.program_phases || []).sort((a: any, b: any) => a.order_index - b.order_index)) {
    const { data: newPhase } = await supabase
      .from('program_phases')
      .insert({ program_id: newProgram.id, name: phase.name, description: phase.description, order_index: phase.order_index, duration_days: phase.duration_days, unlock_type: phase.unlock_type })
      .select('id')
      .single();

    if (newPhase && phase.phase_activities?.length) {
      const activities = phase.phase_activities.sort((a: any, b: any) => a.order_index - b.order_index).map((act: any) => ({
        phase_id: newPhase.id, activity_type: act.activity_type, activity_id: act.activity_id, order_index: act.order_index, is_required: act.is_required, title_override: act.title_override,
      }));
      await supabase.from('phase_activities').insert(activities);
    }
  }

  return ok(newProgram.id);
}

async function translateText(text: string | null, targetLanguage: string): Promise<string | null> {
  if (!text) return null;
  try {
    const { data, error } = await supabase.functions.invoke('translate-single', {
      body: { text, targetLanguage }
    });
    if (error || !data?.translation) return text; // Fallback to original
    return data.translation;
  } catch {
    return text; // Fallback to original on error
  }
}

export async function translateProgram(programId: string, targetLanguage: string): Promise<ApiResult<string>> {
  const { data: program, error: fetchError } = await supabase
    .from('programs')
    .select(`*, program_phases(*, phase_activities(*))`)
    .eq('id', programId)
    .single();

  if (fetchError || !program) return err(ApiError.fromSupabaseError(fetchError || { message: 'Program not found' }));

  // Fetch all content in target language for matching by title
  const [articlesRes, videosRes, audioRes, assessmentsRes] = await Promise.all([
    supabase.from('articles').select('id, title').eq('language', targetLanguage),
    supabase.from('video_files').select('id, title').eq('language', targetLanguage),
    supabase.from('audio_files').select('id, title').eq('language', targetLanguage),
    supabase.from('assessments').select('id, title').eq('language', targetLanguage),
  ]);

  // Fetch original content titles for matching
  const [origArticlesRes, origVideosRes, origAudioRes, origAssessmentsRes] = await Promise.all([
    supabase.from('articles').select('id, title').eq('language', program.language),
    supabase.from('video_files').select('id, title').eq('language', program.language),
    supabase.from('audio_files').select('id, title').eq('language', program.language),
    supabase.from('assessments').select('id, title').eq('language', program.language),
  ]);

  // Create maps for quick lookup
  const targetContentMaps = {
    article: new Map((articlesRes.data || []).map(a => [a.title.toLowerCase().trim(), a.id])),
    video: new Map((videosRes.data || []).map(v => [v.title.toLowerCase().trim(), v.id])),
    audio: new Map((audioRes.data || []).map(a => [a.title.toLowerCase().trim(), a.id])),
    assessment: new Map((assessmentsRes.data || []).map(a => [a.title.toLowerCase().trim(), a.id])),
  };

  const originalContentMaps = {
    article: new Map((origArticlesRes.data || []).map(a => [a.id, a.title])),
    video: new Map((origVideosRes.data || []).map(v => [v.id, v.title])),
    audio: new Map((origAudioRes.data || []).map(a => [a.id, a.title])),
    assessment: new Map((origAssessmentsRes.data || []).map(a => [a.id, a.title])),
  };

  // Helper to find matching content in target language
  const findMatchingContent = (activityType: string, activityId: string): string => {
    // Action types don't need matching - keep original ID
    if (['craving_log', 'cigarette_log', 'mood_log'].includes(activityType)) {
      return activityId;
    }

    const type = activityType as 'article' | 'video' | 'audio' | 'assessment';
    const originalTitle = originalContentMaps[type]?.get(activityId);
    
    if (!originalTitle) {
      // Original content not found, keep original activity_id
      console.log(`Original content not found for ${activityType}:${activityId}, keeping original`);
      return activityId;
    }
    
    // Try to find matching content by title in target language
    const matchingId = targetContentMaps[type]?.get(originalTitle.toLowerCase().trim());
    
    if (matchingId) {
      console.log(`Found matching ${activityType} content: ${originalTitle} -> ${matchingId}`);
      return matchingId;
    }
    
    // No matching content found in target language, keep original activity_id
    // Admin will need to manually update this later
    console.log(`No matching ${activityType} content found for "${originalTitle}" in ${targetLanguage}, keeping original`);
    return activityId;
  };

  // Translate program name and description
  const _langSuffix = targetLanguage.toUpperCase();
  const [translatedName, translatedDesc] = await Promise.all([
    translateText(program.name, targetLanguage),
    translateText(program.description, targetLanguage),
  ]);

  const { data: newProgram, error: programError } = await supabase
    .from('programs')
    .insert({ 
      name: translatedName || program.name, 
      description: translatedDesc, 
      language: targetLanguage, 
      is_active: false, 
      group_id: program.group_id,
      product_id: program.product_id,
      cover_image: program.cover_image,
    })
    .select('id')
    .single();

  if (programError || !newProgram) return err(ApiError.fromSupabaseError(programError || { message: 'Failed to create translated program' }));

  // Copy phases with translation
  for (const phase of (program.program_phases || []).sort((a: any, b: any) => a.order_index - b.order_index)) {
    const [translatedPhaseName, translatedPhaseDesc] = await Promise.all([
      translateText(phase.name, targetLanguage),
      translateText(phase.description, targetLanguage),
    ]);

    const { data: newPhase } = await supabase
      .from('program_phases')
      .insert({ 
        program_id: newProgram.id, 
        name: translatedPhaseName || phase.name, 
        description: translatedPhaseDesc, 
        order_index: phase.order_index, 
        duration_days: phase.duration_days, 
        unlock_type: phase.unlock_type 
      })
      .select('id')
      .single();

    if (newPhase && phase.phase_activities?.length) {
      // Translate title_overrides and find matching content
      const activitiesWithTranslation = await Promise.all(
        phase.phase_activities.sort((a: any, b: any) => a.order_index - b.order_index).map(async (act: any) => {
          const matchedActivityId = findMatchingContent(act.activity_type, act.activity_id);
          const translatedTitleOverride = act.title_override 
            ? await translateText(act.title_override, targetLanguage) 
            : null;

          return {
            phase_id: newPhase.id, 
            activity_type: act.activity_type, 
            activity_id: matchedActivityId, 
            order_index: act.order_index, 
            is_required: act.is_required, 
            title_override: translatedTitleOverride,
          };
        })
      );
      
      console.log(`Inserting ${activitiesWithTranslation.length} activities for phase ${newPhase.id}`);
      const { error: activitiesError } = await supabase.from('phase_activities').insert(activitiesWithTranslation);
      
      if (activitiesError) {
        console.error(`Failed to insert activities for phase ${newPhase.id}:`, activitiesError);
      } else {
        console.log(`Successfully inserted ${activitiesWithTranslation.length} activities`);
      }
    }
  }

  return ok(newProgram.id);
}

// ============ Admin Groups ============

export interface AdminGroup {
  id: string;
  name: string;
  slug: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  website: string | null;
  logo_url: string | null;
  created_at: string | null;
  product_id?: string | null;
  subgroups?: AdminSubgroup[];
}

export interface AdminSubgroup {
  id: string;
  group_id: string;
  name: string;
  slug: string;
  description: string | null;
  created_at: string;
}

export async function getAdminGroups(): Promise<ApiResult<AdminGroup[]>> {
  return query(async () => supabase.from('groups').select('*').order('created_at', { ascending: false }));
}

export async function getAdminGroupById(id: string): Promise<ApiResult<AdminGroup>> {
  return query(async () => supabase.from('groups').select('*').eq('id', id).single());
}

export async function createGroup(data: Omit<AdminGroup, 'id' | 'created_at'>): Promise<ApiResult<AdminGroup>> {
  return mutate(async () => supabase.from('groups').insert([data]).select().single());
}

export async function updateGroup(id: string, data: Partial<AdminGroup>): Promise<ApiResult<null>> {
  const result = await supabase.from('groups').update(data).eq('id', id);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function deleteGroup(id: string): Promise<ApiResult<null>> {
  const result = await supabase.from('groups').delete().eq('id', id);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

// ============ Admin Audio ============

export interface AdminAudioFile {
  id: string;
  title: string;
  description: string | null;
  category: string;
  audio_url: string | null;
  duration: number | null;
  published: boolean | null;
  created_at: string | null;
  article_id: string | null;
  language: string;
  cover_image: string | null;
  product_id?: string | null;
}

export async function getAdminAudioFiles(language?: string): Promise<ApiResult<AdminAudioFile[]>> {
  let queryBuilder = supabase.from('audio_files').select('*').order('created_at', { ascending: false });
  if (language && language !== 'all') queryBuilder = queryBuilder.eq('language', language);
  return query(async () => queryBuilder);
}

export async function getAdminAudioById(id: string): Promise<ApiResult<AdminAudioFile>> {
  return query(async () => supabase.from('audio_files').select('*').eq('id', id).maybeSingle());
}

export async function createAudioFile(data: Omit<AdminAudioFile, 'id' | 'created_at'>): Promise<ApiResult<AdminAudioFile>> {
  return mutate(async () => supabase.from('audio_files').insert([data]).select().single());
}

export async function updateAudioFile(id: string, data: Partial<AdminAudioFile>): Promise<ApiResult<null>> {
  const result = await supabase.from('audio_files').update(data).eq('id', id);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function deleteAudioFile(id: string): Promise<ApiResult<null>> {
  const result = await supabase.from('audio_files').delete().eq('id', id);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function toggleAudioPublish(id: string, published: boolean): Promise<ApiResult<null>> {
  const result = await supabase.from('audio_files').update({ published }).eq('id', id);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function duplicateAudio(id: string): Promise<ApiResult<AdminAudioFile>> {
  const audioResult = await getAdminAudioById(id);
  if (!audioResult.success) return audioResult;

  const original = audioResult.data;

  const duplicate: Omit<AdminAudioFile, 'id' | 'created_at'> = {
    title: `Copy of ${original.title}`,
    description: original.description,
    audio_url: original.audio_url,
    cover_image: original.cover_image,
    category: original.category,
    language: original.language,
    duration: original.duration,
    article_id: original.article_id,
    published: false,
  };

  return createAudioFile(duplicate);
}

export async function duplicateVideo(id: string): Promise<ApiResult<AdminVideoFile>> {
  const videoResult = await getAdminVideoById(id);
  if (!videoResult.success) return videoResult;

  const original = videoResult.data;

  const duplicate: Omit<AdminVideoFile, 'id' | 'created_at'> = {
    title: `Copy of ${original.title}`,
    description: original.description,
    video_url: original.video_url,
    cover_image: original.cover_image,
    category: original.category,
    language: original.language,
    duration: original.duration,
    article_id: original.article_id,
    published: false,
    is_vr: original.is_vr,
    vr_projection: original.vr_projection,
    vr_stereo_mode: original.vr_stereo_mode,
  };

  return createVideoFile(duplicate);
}

export async function uploadAudioToStorage(file: File): Promise<ApiResult<string>> {
  const fileName = `audio-${Date.now()}.${file.name.split('.').pop()}`;
  const { error } = await supabase.storage.from('audio').upload(fileName, file, { contentType: file.type, upsert: false });
  if (error) return err(ApiError.fromSupabaseError(error));
  const { data: { publicUrl } } = supabase.storage.from('audio').getPublicUrl(fileName);
  return ok(publicUrl);
}

// ============ Admin Video ============

export interface AdminVideoFile {
  id: string;
  title: string;
  description: string | null;
  category: string;
  language: string;
  video_url: string | null;
  duration: number | null;
  published: boolean | null;
  created_at: string | null;
  article_id: string | null;
  is_vr: boolean | null;
  vr_projection: string | null;
  vr_stereo_mode: string | null;
  cover_image: string | null;
  product_id?: string | null;
  original_filename?: string | null;
}

export async function getAdminVideoFiles(): Promise<ApiResult<AdminVideoFile[]>> {
  return query(async () => supabase.from('video_files').select('*').order('created_at', { ascending: false }));
}

export async function getAdminVideoById(id: string): Promise<ApiResult<AdminVideoFile>> {
  return query(async () => supabase.from('video_files').select('*').eq('id', id).single());
}

export async function createVideoFile(data: Omit<AdminVideoFile, 'id' | 'created_at'>): Promise<ApiResult<AdminVideoFile>> {
  return mutate(async () => supabase.from('video_files').insert([data]).select().single());
}

export async function updateVideoFile(id: string, data: Partial<AdminVideoFile>): Promise<ApiResult<null>> {
  const result = await supabase.from('video_files').update(data).eq('id', id);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function deleteVideoFile(id: string): Promise<ApiResult<null>> {
  const result = await supabase.from('video_files').delete().eq('id', id);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function toggleVideoPublish(id: string, published: boolean): Promise<ApiResult<null>> {
  const result = await supabase.from('video_files').update({ published }).eq('id', id);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export interface VideoUploadResult {
  url: string;
  originalFilename: string;
}

export async function uploadVideoToStorage(
  file: File,
  onProgress?: (percentage: number) => void
): Promise<ApiResult<VideoUploadResult>> {
  const fileExt = file.name.split('.').pop();
  const fileName = `${crypto.randomUUID()}.${fileExt}`;
  const originalFilename = file.name;
  
  // Use TUS resumable upload for large files (>50MB) to bypass API payload limits
  const LARGE_FILE_THRESHOLD = 50 * 1024 * 1024; // 50MB
  
  if (file.size > LARGE_FILE_THRESHOLD) {
    // Get current session for auth token
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    
    if (!accessToken) {
      return err(new ApiError('NOT_AUTHENTICATED', 'You must be logged in to upload large files'));
    }

    return new Promise((resolve) => {
      const upload = new tus.Upload(file, {
        endpoint: `${SUPABASE_URL}/storage/v1/upload/resumable`,
        retryDelays: [0, 3000, 5000, 10000, 20000],
        headers: {
          authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
        },
        uploadDataDuringCreation: true,
        removeFingerprintOnSuccess: true,
        chunkSize: 6 * 1024 * 1024, // 6MB chunks
        metadata: {
          bucketName: 'videos',
          objectName: fileName,
          contentType: file.type || 'video/mp4',
          cacheControl: '3600',
        },
        onError: (error) => {
          console.error('TUS upload error:', error);
          // Check for 413 error which means global file size limit exceeded
          const errorMessage = error.message || '';
          if (errorMessage.includes('413') || errorMessage.includes('Maximum size exceeded')) {
            resolve(err(new ApiError('FILE_TOO_LARGE', 
              'File exceeds the global size limit. Please increase the Global file size limit in Supabase Storage Settings (requires Pro Plan for files >50MB).'
            )));
          } else {
            resolve(err(new ApiError('UPLOAD_ERROR', errorMessage || 'Failed to upload video')));
          }
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          const percentage = Math.round((bytesUploaded / bytesTotal) * 100);
          onProgress?.(percentage);
        },
        onSuccess: () => {
          const { data } = supabase.storage.from('videos').getPublicUrl(fileName);
          resolve(ok({ url: data.publicUrl, originalFilename }));
        },
      });
      
      // Check for previous uploads to resume
      upload.findPreviousUploads().then((previousUploads) => {
        if (previousUploads.length) {
          upload.resumeFromPreviousUpload(previousUploads[0]);
        }
        upload.start();
      });
    });
  } else {
    // Standard upload for smaller files (faster for small files)
    const { error } = await supabase.storage.from('videos').upload(fileName, file);
    if (error) return err(ApiError.fromSupabaseError(error));
    
    onProgress?.(100);
    const { data } = supabase.storage.from('videos').getPublicUrl(fileName);
    return ok({ url: data.publicUrl, originalFilename });
  }
}

// ============ Admin Assessments ============

export interface AdminAssessment {
  id: string;
  title: string;
  description: string | null;
  language: string;
  published: boolean | null;
  created_at: string | null;
  cover_image: string | null;
  product_id?: string | null;
}

export interface AssessmentField {
  id: string;
  assessment_id: string;
  label: string;
  field_type: string;
  required: boolean;
  order_index: number;
  options?: any;
  min_value?: number | null;
  max_value?: number | null;
}

export async function getAdminAssessments(): Promise<ApiResult<AdminAssessment[]>> {
  return query(async () => supabase.from('assessments').select('*').order('created_at', { ascending: false }));
}

export async function getAssessmentFields(assessmentId: string): Promise<ApiResult<AssessmentField[]>> {
  const result = await supabase.from('assessment_fields').select('*').eq('assessment_id', assessmentId).order('order_index');
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(result.data as AssessmentField[]);
}

export async function deleteAssessment(id: string): Promise<ApiResult<null>> {
  const result = await supabase.from('assessments').delete().eq('id', id);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function toggleAssessmentPublish(id: string, published: boolean): Promise<ApiResult<null>> {
  const result = await supabase.from('assessments').update({ published }).eq('id', id);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function submitAssessmentResponses(responses: Array<{ assessment_id: string; user_id: string; field_id: string; response: any }>): Promise<ApiResult<null>> {
  const result = await supabase.from('assessment_responses').insert(responses);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

// ============ Admin Allowed Emails ============

export interface AllowedEmail {
  id: string;
  email: string;
  group_id: string | null;
  subgroup_id: string | null;
  is_used: boolean | null;
  used_at: string | null;
  created_at: string | null;
  created_by: string | null;
  group?: { name: string } | null;
}

export async function getAllowedEmails(): Promise<ApiResult<AllowedEmail[]>> {
  return query(async () => supabase.from('allowed_emails').select('*, group:groups(name)').order('created_at', { ascending: false }));
}

export async function createAllowedEmails(emails: Array<{ email: string; group_id: string | null; subgroup_id?: string | null; created_by: string | null }>): Promise<ApiResult<null>> {
  const result = await supabase.from('allowed_emails').insert(emails);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function deleteAllowedEmail(id: string): Promise<ApiResult<null>> {
  const result = await supabase.from('allowed_emails').delete().eq('id', id);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

// ============ Admin Invite Codes ============

export interface InviteCode {
  id: string;
  code: string;
  group_id: string;
  subgroup_id: string | null;
  max_uses: number | null;
  uses_count: number;
  is_active: boolean;
  expires_at: string | null;
  created_at: string;
  created_by: string | null;
  allowed_products: string[];
  group?: { name: string };
}

export async function getInviteCodes(): Promise<ApiResult<InviteCode[]>> {
  return query(async () => supabase.from('invite_codes').select('*, group:groups(name)').order('created_at', { ascending: false })) as Promise<ApiResult<InviteCode[]>>;
}

export async function createInviteCode(data: { code: string; group_id: string; subgroup_id?: string | null; max_uses: number | null; expires_at: string | null; created_by: string | null; allowed_products: string[] }): Promise<ApiResult<InviteCode>> {
  return mutate(async () => supabase.from('invite_codes').insert([data]).select('*, group:groups(name)').single());
}

export async function toggleInviteCodeActive(id: string, is_active: boolean): Promise<ApiResult<null>> {
  const result = await supabase.from('invite_codes').update({ is_active }).eq('id', id);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function updateInviteCode(id: string, data: { 
  group_id?: string; 
  subgroup_id?: string | null; 
  max_uses?: number | null; 
  expires_at?: string | null; 
  is_active?: boolean;
  allowed_products?: string[];
}): Promise<ApiResult<InviteCode>> {
  return mutate(async () => supabase.from('invite_codes').update(data).eq('id', id).select('*, group:groups(name)').single());
}

export async function deleteInviteCode(id: string): Promise<ApiResult<null>> {
  const result = await supabase.from('invite_codes').delete().eq('id', id);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

// ============ Admin User Roles ============

export type AppRole = 'platform_superadmin' | 'brand_admin' | 'group_admin' | 'subgroup_admin' | 'user';

/**
 * UserRole interface matching database schema
 * The database user_roles table uses 'role_type' column
 */
export interface UserRole {
  id: string;
  user_id: string;
  role_type: AppRole;
  brand_id?: string | null;
  group_id: string | null;
  subgroup_id: string | null;
  granted_by?: string | null;
  created_at: string | null;
  updated_at?: string | null;
}

/**
 * Extended UserRole with email (for display purposes)
 */
export interface UserRoleWithEmail extends UserRole {
  email: string | null;
}

export async function getUserRoles(): Promise<ApiResult<UserRole[]>> {
  const result = await supabase.from('user_roles').select('*').order('created_at', { ascending: false });
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  // Cast to UserRole[] - the database schema matches our interface
  return ok((result.data || []) as unknown as UserRole[]);
}

export async function assignUserRole(
  userId: string, 
  role: AppRole, 
  brandId?: string | null,
  groupId?: string | null, 
  subgroupId?: string | null
): Promise<ApiResult<null>> {
  const { data: existing } = await supabase.from('user_roles').select('id').eq('user_id', userId).maybeSingle();
  
  // Build role data based on role type and check constraint requirements
  // platform_superadmin, user: brand_id=NULL, group_id=NULL, subgroup_id=NULL
  // brand_admin: brand_id NOT NULL, group_id=NULL, subgroup_id=NULL
  // group_admin: group_id NOT NULL
  // subgroup_admin: group_id NOT NULL, subgroup_id NOT NULL
  const roleData: Record<string, string | null> = { 
    role_type: role,
    brand_id: role === 'brand_admin' ? (brandId || null) : null,
    group_id: (role === 'group_admin' || role === 'subgroup_admin') ? (groupId || null) : null, 
    subgroup_id: role === 'subgroup_admin' ? (subgroupId || null) : null 
  };
  
  if (existing) {
    const result = await supabase.from('user_roles').update(roleData).eq('user_id', userId);
    if (result.error) return err(ApiError.fromSupabaseError(result.error));
  } else {
    const result = await supabase.from('user_roles').insert([{ user_id: userId, ...roleData } as any]);
    if (result.error) return err(ApiError.fromSupabaseError(result.error));
  }
  return ok(null);
}

export async function deleteUserRole(roleId: string): Promise<ApiResult<null>> {
  const result = await supabase.from('user_roles').delete().eq('id', roleId);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

// ============ Content Options for Program Editor ============

export interface ContentItem {
  id: string;
  title: string;
  language?: string;
  product_scope?: string | null; // 'all' | 'mindcotine' | 'mindco-relief'
}

export async function getProgramEditorContent(productId?: string): Promise<ApiResult<{ groups: ContentItem[]; articles: ContentItem[]; videos: ContentItem[]; audioFiles: ContentItem[]; assessments: ContentItem[] }>> {
  // Build queries for each content type
  let articlesQuery = supabase.from('articles').select('id, title, language, product_scope').eq('published', true).order('title');
  let videosQuery = supabase.from('video_files').select('id, title, language, product_scope').eq('published', true).order('title');
  let audioQuery = supabase.from('audio_files').select('id, title, language, product_scope').eq('published', true).order('title');
  let assessmentsQuery = supabase.from('assessments').select('id, title, language, product_scope').eq('published', true).order('title');

  // Filter by product scope: show 'all' OR matching product_id
  if (productId && productId !== 'none') {
    articlesQuery = articlesQuery.or(`product_scope.eq.all,product_scope.eq.${productId}`);
    videosQuery = videosQuery.or(`product_scope.eq.all,product_scope.eq.${productId}`);
    audioQuery = audioQuery.or(`product_scope.eq.all,product_scope.eq.${productId}`);
    assessmentsQuery = assessmentsQuery.or(`product_scope.eq.all,product_scope.eq.${productId}`);
  }

  const [groupsRes, articlesRes, videosRes, audioRes, assessmentsRes] = await Promise.all([
    supabase.from('groups').select('id, name').order('name'),
    articlesQuery,
    videosQuery,
    audioQuery,
    assessmentsQuery,
  ]);

  if (groupsRes.error) return err(ApiError.fromSupabaseError(groupsRes.error));

  return ok({
    groups: (groupsRes.data || []).map(g => ({ id: g.id, title: g.name })),
    articles: (articlesRes.data || []).map(a => ({ id: a.id, title: a.title, language: a.language, product_scope: a.product_scope })),
    videos: (videosRes.data || []).map(v => ({ id: v.id, title: v.title, language: v.language, product_scope: v.product_scope })),
    audioFiles: (audioRes.data || []).map(a => ({ id: a.id, title: a.title, language: a.language, product_scope: a.product_scope })),
    assessments: (assessmentsRes.data || []).map(a => ({ id: a.id, title: a.title, language: a.language, product_scope: a.product_scope })),
  });
}

// ============ Edge Function Invocations ============

export async function invokeEnrichArticle(title: string, content: string): Promise<ApiResult<{ enrichedContent: string }>> {
  const { data, error } = await supabase.functions.invoke('enrich-article', { body: { title, currentContent: content } });
  if (error) return err(ApiError.fromSupabaseError(error));
  return ok(data);
}

export async function invokeGeneratePodcast(articleTitle: string, articleContent: string, category: string): Promise<ApiResult<{ success: boolean; error?: string }>> {
  const { data, error } = await supabase.functions.invoke('generate-podcast', { body: { articleTitle, articleContent, category } });
  if (error) return err(ApiError.fromSupabaseError(error));
  return ok(data);
}

export async function invokeGeneratePodcastForArticle(articleId: string, targetLanguage: string, timeLength: string): Promise<ApiResult<{ success: boolean; error?: string }>> {
  const { data, error } = await supabase.functions.invoke('generate-podcast', { body: { articleId, targetLanguage, timeLength } });
  if (error) return err(ApiError.fromSupabaseError(error));
  return ok(data);
}

export async function invokeBulkGeneratePodcasts(sourceLanguage: string, targetLanguage: string, timeLength: string): Promise<ApiResult<{ generated?: number; total?: number }>> {
  const { data, error } = await supabase.functions.invoke('generate-all-podcasts', { body: { sourceLanguage, targetLanguage, timeLength } });
  if (error) return err(ApiError.fromSupabaseError(error));
  return ok(data);
}

export async function invokeTranslateArticle(articleId: string, targetLanguage: string): Promise<ApiResult<any>> {
  const { data, error } = await supabase.functions.invoke('translate-article', { body: { articleId, targetLanguage } });
  if (error) return err(ApiError.fromSupabaseError(error));
  return ok(data);
}

export async function invokeBulkTranslateArticles(sourceLanguage: string, targetLanguage: string): Promise<ApiResult<{ translatedCount: number; totalArticles: number }>> {
  const { data, error } = await supabase.functions.invoke('translate-all-articles', { body: { sourceLanguage, targetLanguage } });
  if (error) return err(ApiError.fromSupabaseError(error));
  return ok(data);
}

export async function invokeTranslateAudio(audioId: string, targetLanguage: string, timeLength: string): Promise<ApiResult<any>> {
  const { data, error } = await supabase.functions.invoke('translate-audio-v3', { body: { audioId, targetLanguage, timeLength } });
  if (error) return err(ApiError.fromSupabaseError(error));
  return ok(data);
}

export async function invokeBulkTranslateAudio(sourceLanguage: string, targetLanguage: string, timeLength: string): Promise<ApiResult<any>> {
  const { data, error } = await supabase.functions.invoke('translate-all-audio-v3', { body: { sourceLanguage, targetLanguage, timeLength } });
  if (error) return err(ApiError.fromSupabaseError(error));
  return ok(data);
}

export async function invokeTranslateVideo(videoId: string, targetLanguage: string): Promise<ApiResult<any>> {
  const { data, error } = await supabase.functions.invoke('translate-video', {
    body: { videoId, targetLanguage }
  });
  if (error) return err(ApiError.fromSupabaseError(error));
  return ok(data);
}

export async function invokeTranslateAssessment(assessmentId: string, targetLanguage: string): Promise<ApiResult<any>> {
  const { data, error } = await supabase.functions.invoke('translate-assessment', {
    body: { assessmentId, targetLanguage }
  });
  if (error) return err(ApiError.fromSupabaseError(error));
  return ok(data);
}

export async function invokeGetUsers(accessToken: string): Promise<ApiResult<Array<{ id: string; email: string; group_id: string | null; subgroup_id: string | null; full_name: string | null }>>> {
  const response = await fetch(`https://vdvkrxwohdtgxheehlki.supabase.co/functions/v1/get-users`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const errorData = await response.json();
    return err(new ApiError(errorData.error || 'Failed to fetch users', 'FETCH_USERS_ERROR'));
  }

  const data = await response.json();
  return ok(data);
}

export async function invokeGetAnalyticsWithUsers(params: { 
  startTime: string; 
  eventType: string | null; 
  userEmail: string | null; 
  productId?: string | null;
  groupId?: string | null;
  subgroupId?: string | null;
  limit: number;
}): Promise<ApiResult<{ events: any[] }>> {
  const { data, error } = await supabase.functions.invoke('get-analytics-with-users', { body: params });
  if (error) return err(ApiError.fromSupabaseError(error));
  return ok(data);
}

// ============ Storage Helpers ============

export async function createSignedAudioUrl(path: string): Promise<ApiResult<string>> {
  const { data, error } = await supabase.storage.from('audio').createSignedUrl(path, 60 * 60);
  if (error) return err(ApiError.fromSupabaseError(error));
  return ok(data.signedUrl);
}

// ============ AI Chat ============

export async function invokeAiChat(message: string): Promise<ApiResult<{ response: string }>> {
  const { data, error } = await supabase.functions.invoke('ai-chat', { body: { message } });
  if (error) return err(ApiError.fromSupabaseError(error));
  return ok(data);
}

// ============ TTS Test ============

export async function invokeTtsTest(text: string, voiceName: string): Promise<ApiResult<{ success: boolean; audio_b64?: string; mimeType?: string; bytes?: number; error?: string }>> {
  const { data, error } = await supabase.functions.invoke('tts-test', { body: { text, voiceName } });
  if (error) return err(ApiError.fromSupabaseError(error));
  return ok(data);
}

// ============ Translation Management ============

export async function invokePopulateTranslations(language?: string): Promise<ApiResult<{ inserted?: number }>> {
  const { data, error } = await supabase.functions.invoke('populate-translations', { body: language ? { language } : {} });
  if (error) return err(ApiError.fromSupabaseError(error));
  return ok(data || {});
}

export async function invokeAuditTranslationKeys(accessToken: string): Promise<ApiResult<any>> {
  const { data, error } = await supabase.functions.invoke('audit-translation-keys', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (error) return err(ApiError.fromSupabaseError(error));
  return ok(data);
}

export async function getTranslationsWithPagination(): Promise<ApiResult<Array<{ key: string; language: string; value: string }>>> {
  let allData: Array<{ key: string; language: string; value: string }> = [];
  let page = 0;
  const pageSize = 1000;
  let hasMore = true;

  while (hasMore) {
    const { data, error } = await supabase
      .from('translations')
      .select('key, language, value')
      .range(page * pageSize, (page + 1) * pageSize - 1);

    if (error) return err(ApiError.fromSupabaseError(error));

    if (data && data.length > 0) {
      allData = [...allData, ...data];
      hasMore = data.length === pageSize;
      page++;
    } else {
      hasMore = false;
    }
  }

  return ok(allData);
}

export async function deleteTranslationsByKeys(keys: string[]): Promise<ApiResult<null>> {
  const result = await supabase.from('translations').delete().in('key', keys);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function upsertTranslations(translations: Array<{ key: string; language: string; value: string; section: string }>): Promise<ApiResult<number>> {
  const { data, error } = await supabase.from('translations').upsert(translations, { onConflict: 'language,key', ignoreDuplicates: true }).select('key, language');
  if (error) return err(ApiError.fromSupabaseError(error));
  return ok(data?.length ?? translations.length);
}

export async function getEnglishTranslations(): Promise<ApiResult<Array<{ key: string; value: string; section: string }>>> {
  const { data, error } = await supabase.from('translations').select('key, value, section').eq('language', 'en');
  if (error) return err(ApiError.fromSupabaseError(error));
  return ok(data || []);
}

// ============ Assessment Analytics ============

export async function getAssessmentResponses(assessmentId: string): Promise<ApiResult<Array<{ field_id: string; user_id: string; response: any; created_at: string | null }>>> {
  const { data, error } = await supabase.from('assessment_responses').select('*').eq('assessment_id', assessmentId);
  if (error) return err(ApiError.fromSupabaseError(error));
  return ok(data || []);
}

export async function getAdminAssessmentById(id: string): Promise<ApiResult<AdminAssessment>> {
  return query(async () => supabase.from('assessments').select('*').eq('id', id).single());
}

export async function createAssessment(data: { title: string; description?: string | null; language: string; cover_image?: string | null; assessment_type?: string | null; is_required?: boolean | null; show_on_signup?: boolean | null; product_scope?: string | null }): Promise<ApiResult<{ id: string }>> {
  return mutate(async () => supabase.from('assessments').insert(data).select('id').single());
}

export async function updateAssessment(id: string, data: { title?: string; description?: string | null; language?: string; cover_image?: string | null; assessment_type?: string | null; is_required?: boolean | null; show_on_signup?: boolean | null; product_scope?: string | null }): Promise<ApiResult<null>> {
  const result = await supabase.from('assessments').update(data).eq('id', id);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function deleteAssessmentFields(assessmentId: string): Promise<ApiResult<null>> {
  const result = await supabase.from('assessment_fields').delete().eq('assessment_id', assessmentId);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}

export async function createAssessmentFields(fields: Array<{ assessment_id: string; field_type: string; label: string; options?: any; required?: boolean; order_index: number; min_value?: number | null; max_value?: number | null }>): Promise<ApiResult<null>> {
  const result = await supabase.from('assessment_fields').insert(fields);
  if (result.error) return err(ApiError.fromSupabaseError(result.error));
  return ok(null);
}
